import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useForm } from "react-hook-form";
import { useEyeconxStore } from "../../../store/eyeconxStore";
import bookingService from "../../../services/booking.service";
import Loading from "../../common/Loading";

const NotesModal = ({ patientBooking, show, onClose,handleUpdate }) => {
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState(false);
    const clinicId = useEyeconxStore((state) => state.user.clinicId);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: { clinicComments: "" },
    });

    // ✅ Populate form with current patientBooking info
    useEffect(() => {
        if (patientBooking && show) {
            reset({
                clinicComments: patientBooking.clinicComments || "",
            });
        }
    }, [patientBooking, show, reset]);

    if (!show) return null;

    // ✅ Handle Submit
    const onSubmitComments = async (data) => {
        setLoading(true);
        setSaved(false);

        const updatedRequest = {
            ...patientBooking,
            clinicId: clinicId,
            clinicComments: data.clinicComments || "",
        };

        try {
            await bookingService.updateBookingRequests(clinicId, updatedRequest);
            setSaved(true);
            handleUpdate();
            // Optional: update local UI or refetch parent data
            setTimeout(() => {
                setSaved(false);
                onClose();
            }, 1200);
        } catch (error) {
            console.error("Error updating booking:", error);
        } finally {
            setLoading(false);
        }
    };

    return ReactDOM.createPortal(
        <div
            className={`modal fade ${show ? "show d-block" : ""}`}
            tabIndex="-1"
            role="dialog"
            style={{
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 1050,
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
            }}
        >
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div className="modal-content rounded-3 shadow-lg border-0">
                    <div className="modal-header bg-primary text-white">
                        <h5 className="modal-title fw-bold text-white">
                            <i className="bi bi-journal-text me-2"></i>
                            Add Clinic Notes / Comments
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-lg btn-close-white"
                            onClick={onClose}
                            aria-label="Close"
                        ></button>
                    </div>

                    <div className="modal-body p-4">
                        {loading ? (
                            <Loading />
                        ) : (
                            <form onSubmit={handleSubmit(onSubmitComments)}>
                                {/* Patient info summary */}
                                <div className="mb-3">
                                    <h6 className="fw-semibold text-muted">
                                        {patientBooking.firstName} {patientBooking.lastName}{" "}
                                        <small className="text-muted">({patientBooking.doctorName})</small>
                                    </h6>
                                </div>

                                {/* Clinic Comments */}
                                <div className="mb-3">
                                    <label htmlFor="clinicComments" className="form-label fw-semibold">
                                        Clinic Notes / Comments
                                    </label>
                                    <textarea
                                        id="clinicComments"
                                        className={`form-control ${errors.clinicComments ? "is-invalid" : ""}`}
                                        rows="5"
                                        {...register("clinicComments", {
                                            required: "Please enter a comment or note",
                                            maxLength: {
                                                value: 500,
                                                message: "Comments cannot exceed 500 characters",
                                            },
                                        })}
                                        placeholder="Add internal notes or comments about this booking request..."
                                    ></textarea>
                                    {errors.clinicComments && (
                                        <div className="invalid-feedback">
                                            {errors.clinicComments.message}
                                        </div>
                                    )}
                                    <div className="form-text">
                                        These notes are visible only to clinic staff.
                                    </div>
                                </div>

                                {/* Save Button */}
                                <div className="text-end">
                                    <button type="button" className="btn btn-secondary me-2" onClick={onClose}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        <i className="bi bi-save me-1"></i> Save Notes
                                    </button>
                                </div>

                                {saved && (
                                    <div className="alert alert-success mt-3 p-2">
                                        <i className="bi bi-check-circle me-2"></i>Notes updated successfully!
                                    </div>
                                )}
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body //  ensures modal overlays the entire page
    );
};

export default NotesModal;
